import { AxiosError } from "axios";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "react-query";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import * as Sentry from "@sentry/react";


import { ErrorPage, ProtectedRoute } from "@/components";
import {
  AuthProvider,
  NotificationsProvider,
  ResponsivenessProvider,
  ScrollPositionProvider,
  ThemeProvider,
  TranslateProvider,
  UserProvider,
} from "@/providers";
import {
  Audio,
  Calendar,
  Collection,
  Community,
  Content,
  DataPrivacy,
  Favourites,
  ForSale,
  ManageSubscription,
  Playlist,
  Playlists,
  Profile,
  PurchaseSuccess,
  RecentlyWatched,
  ResetPassword,
  Root,
  Search,
  SignIn,
  SignUp,
  Video,
  WatchLater,
} from "@/routes";

import { PageWithNavBar } from "@/app/modules/Navigation";
import { ProfileUpdate } from "@/routes/ProfileUpdate.tsx";
import { GlobalLoadingWrapper } from "./GlobalLoadingWrapper.tsx";

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(
  createBrowserRouter,
);


const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: <Root />, // All pages are wrapped by the Root component
    errorElement: <ErrorPage />,
    children: [
      {
        path: "", // The following pages are wrapped with the NavBar on the left
        element: (
          <ResponsivenessProvider>
            <NotificationsProvider>
              <PageWithNavBar />
            </NotificationsProvider>
          </ResponsivenessProvider>
        ),
        children: [
          {
            path: "",
            element: <Content />,
          },
          {
            path: "content/:tabId",
            element: <Content />,
          },
          {
            path: "content/:tabId/:subTabId?",
            element: <Content />,
          },
          {
            path: "collection/:id",
            element: <Collection />,
          },
          {
            path: "video/:id",
            element: <Video />,
          },
          {
            path: "audio/:id",
            element: <Audio />,
          },
          {
            path: "search",
            element: <Search />,
          },
          {
            path: "community",
            element: (
              <ProtectedRoute>
                <Community />
              </ProtectedRoute>
            ),
          },
          {
            path: "community/:group",
            element: (
              <ProtectedRoute>
                <Community />
              </ProtectedRoute>
            ),
          },
          {
            path: "community/:group/:postId",
            element: (
              <ProtectedRoute>
                <Community />
              </ProtectedRoute>
            ),
          },
          {
            path: "profile",
            element: <Profile />,
          },
          {
            path: "profile-update",
            element: (
              <ProtectedRoute>
                <ProfileUpdate />
              </ProtectedRoute>
            ),
          },
          {
            path: "data-privacy",
            element: <DataPrivacy />,
          },
          {
            path: "favorites",
            element: (
              <ProtectedRoute>
                <Favourites />
              </ProtectedRoute>
            ),
          },
          {
            path: "calendar",
            element: (
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            ),
          },
          {
            path: "watch",
            element: (
              <ProtectedRoute>
                <WatchLater />
              </ProtectedRoute>
            ),
          },
          {
            path: "recent",
            element: (
              <ProtectedRoute>
                <RecentlyWatched />
              </ProtectedRoute>
            ),
          },
          {
            path: "playlists",
            element: (
              <ProtectedRoute>
                <Playlists />
              </ProtectedRoute>
            ),
          },
          {
            path: "playlist/:id",
            element: (
              <ProtectedRoute>
                <Playlist />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "purchase-success",
        element: (
          <ProtectedRoute>
            <PurchaseSuccess />
          </ProtectedRoute>
        ),
      },
      {
        path: "/sign-in",
        element: <SignIn />,
      },
      {
        path: "/sign-up",
        element: <SignUp />,
      },
      {
        path: "/for-sale",
        element: (
          <ProtectedRoute>
            <ForSale />
          </ProtectedRoute>
        ),
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
      },
      {
        path: "/manage-subscription",
        element: <ManageSubscription />,
      },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 60 second cache for api calls
      retry: (failureCount, error) =>
        ![404, 401, 403].includes(
          (error as AxiosError).response?.status ?? 0,
        ) && failureCount < 3, // Retry 3 times for server-side issues
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <ThemeProvider>
            <TranslateProvider>
              <ScrollPositionProvider>
                <HelmetProvider>
                  <GlobalLoadingWrapper>
                    <RouterProvider router={router} />
                  </GlobalLoadingWrapper>
                </HelmetProvider>
              </ScrollPositionProvider>
            </TranslateProvider>
          </ThemeProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
